import React, { ReactElement, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import theme from "../../../themes";
import { useEnumStrings } from "../../enumStrings";
import {
  useGetAllChamberMembersQuery,
  useGetAllShopLocalCampaignsForChamberQuery,
} from "../../../graphql/types";
import "react-toastify/dist/ReactToastify.css";
import { useHistory, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import Loader from "../../common/Loader/Loader";
import { Typography } from "antd";
import FullPageShopLocalCampaignsTable from "./FullPageShopLocalCampaignsTable";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import VideoTutorialButton from "../../common/VideoTutorialButton";
import ExportButton from "../../common/ExportButton";

export type ShopLocalPageType = "createNew" | "seePrevious";

const useStyles = makeStyles((themes: any) => ({
  title: {
    marginLeft: "24px",
    marginRight: "24px",
    fontSize: "30px",
    color: "white",
    fontWeight: "bold",
    marginBottom: "30px",
    marginTop: "15px",
    display: "flex",
  },
  titleContent: {
    [theme.breakpoints.up("lg")]: {
      paddingTop: "30px",
    },
  },
  content: {
    display: "flex",
    background: "#FFFFFF",
    minHeight: "70vh",
    borderRadius: "16px",
    margin: "20px",
    padding: "30px",
    [theme.breakpoints.down("sm")]: {
      margin: 0,
    },
    marginTop: "33px",
    flexDirection: "column",
  },
  buttonGroupRoot: {
    paddingBottom: "0px",
    "& .MuiToggleButton-root": {
      paddingTop: "5px",
      paddingBottom: "5px",
      backgroundColor: "white",
      color: "#737373",
      fontWeight: "bold",
      fontSize: "17px",
      "&:hover": {
        boxShadow: "3px 2px 5px 0px rgba(0,0,0,0.1)",
        backgroundColor: "white",
      },
      "&.Mui-selected": {
        backgroundColor: "#E1A731",
        color: "white",
        "&:hover": {
          boxShadow: "0px 2px 5px 3px rgba(0,0,0,0.1)",
          backgroundColor: "#E1A731",
        },
      },
    },
  },
}));

const ListAllChamberShopLocalCampaigns = ({
  mode,
}: {
  mode: "active" | "inactive";
}): ReactElement => {
  const classes = useStyles();
  const location = useLocation();
  const bingo = useMemo(() => {
    return (location.state || { showToast: undefined }) as {
      showToast: boolean | undefined;
      toastText?: string;
    };
  }, [location.state]);
  const history = useHistory();
  const enumStrings = useEnumStrings();

  useEffect(() => {
    if (bingo.showToast) {
      toast.success(
        bingo.toastText
          ? bingo.toastText
          : enumStrings.chamberShopLocal.toastText
      );
      history.replace({ ...history.location, state: undefined });
    }
  }, [
    bingo.showToast,
    bingo.toastText,
    enumStrings.chamberShopLocal.toastText,
    history,
  ]);

  const { data: allShopLocalCampaignsForChamber } =
    useGetAllShopLocalCampaignsForChamberQuery({
      fetchPolicy: "network-only",
    });

  const chamberInfo = useGetChamberFromUserIdInAuth();
  const { data: allChamberMemberData } = useGetAllChamberMembersQuery({
    variables: { chamberId: chamberInfo?.id || "" },
    skip: !chamberInfo?.id,
    fetchPolicy: "cache-and-network",
  });

  const headerInfo = useMemo(() => {
    if (mode === "active") {
      return {
        title: enumStrings.chamberShopLocal.activeTitle,
        description: enumStrings.chamberShopLocal.activeDescription,
      };
    }

    return {
      title: "View Previous Contests",
      description:
        "View and download the list of users who participated in the previous Shop Local contest.",
    };
  }, [
    enumStrings.chamberShopLocal.activeDescription,
    enumStrings.chamberShopLocal.activeTitle,
    mode,
  ]);

  return (
    <div>
      <div className={classes.titleContent}>
        <div
          className={classes.title}
          style={{
            flexDirection: "column",
          }}
        >
          <span>{headerInfo.title}</span>
          <span
            style={{
              marginLeft: "1px",
              fontWeight: "normal",
              fontSize: "16px",
            }}
          >
            {headerInfo.description}
          </span>
          {mode === "inactive" && (
            <div
              style={{
                display: "flex",
                gap: "12px",
                flexWrap: "wrap",
                marginTop: "16px",
              }}
            >
              <VideoTutorialButton label="WATCH TUTORIALS" tutorialUrl="" />
              <ExportButton
                customOnClick={() => undefined}
                disablePadding
                icon={
                  <InfoOutlinedIcon
                    style={{
                      color: "#E1A731",
                      marginRight: "10px",
                      width: "24px",
                      height: "24px",
                    }}
                  />
                }
                label="VIEW INSTRUCTIONS"
              />
            </div>
          )}
          <div style={{ paddingTop: "10px" }} />
        </div>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
        />
      </div>
      <div className={classes.content}>
        {!allShopLocalCampaignsForChamber ||
        !allShopLocalCampaignsForChamber ||
        !allChamberMemberData ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              paddingTop: 20,
            }}
          >
            <Loader />
          </div>
        ) : (
          <>
            {(
              allShopLocalCampaignsForChamber?.getAllShopLocalCampaignsForChamber ||
              []
            ).length > 0 ? (
              (allChamberMemberData?.getAllChamberMembers || []).length > 0 && (
                <FullPageShopLocalCampaignsTable
                  allChamberMemberData={allChamberMemberData}
                  allShopLocalCampaignData={
                    allShopLocalCampaignsForChamber?.getAllShopLocalCampaignsForChamber
                  }
                  mode={mode}
                />
              )
            ) : (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Typography
                  style={{
                    fontSize: "20px",
                    fontWeight: "bold",
                  }}
                >
                  {mode === "active"
                    ? enumStrings.shopLocal.fullPageShopLocalCampaign
                        .noActiveContests
                    : enumStrings.shopLocal.fullPageShopLocalCampaign
                        .noPreviousContests}
                </Typography>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ListAllChamberShopLocalCampaigns;
